/**
 * **The client's active plan, as one card** — the client detail's summary column, and the roster
 * preview panel.
 *
 * Promoted here on 1 September 2026, on the owner's instruction that the two are the same card:
 * "tylko rozmiarem oczywiście dostosuj." They were two implementations that happened to look
 * similar, and they had drifted in four ways — the roster printed an untranslated `Strength`, had
 * no scheduling line, lower-cased the week counter and named the horizon by its date instead of by
 * its distance. The KANON v3 version, which is the detail's, is what this draws; the roster's
 * differences were not decisions and are not preserved.
 *
 * ── Two sizes, one card ───────────────────────────────────────────────────────────────────────
 * `size` picks the cover's height and the hero's type scale, and nothing else. Everything
 * structural — the scrim, the chip, the scheduling line, the week counter, the horizon, the bar —
 * is identical, because the difference between the two places is how much room they have, not what
 * they are saying.
 *
 * ── It takes WORDS, not a plan ────────────────────────────────────────────────────────────────
 * `weeksLabel` and `endsLabel` arrive already written. `shared/` has no business vocabulary and no
 * `t`, and the two callers reach their figures by different routes — the detail's `model/` takes a
 * `t`, the roster's deliberately does not — so the card takes the sentence rather than the numbers.
 * What it will not do is decide what a plan IS.
 *
 * ── The `Kalendarz →` link is GONE from both, and the WHOLE CARD replaced it ──────────────────
 * The link stood here as a recorded deviation from v3, argued as the only route from the card to
 * that plan's calendar. Owner, 1 September 2026: it comes off, and it stays off. What that left
 * behind was a screen with no entry into the plan's cycle grid at all — the grid's plan picker sits
 * in its own header, so it only appears once you are already there. Owner, 2 September 2026: "po
 * prostu po kliknięciu na całą kartę ma tam przenosić".
 *
 * So the card is one link, and the mechanics are the roster row's, which is the same problem solved
 * on the same day: a real `<a>` with a real address — so a middle click opens a tab, the address
 * copies, and Tab reaches it — stretched over the card by an overlay rather than by wrapping the
 * content, because wrapping it would put a `<b>`, an `<em>` and a `role="progressbar"` inside the
 * anchor for nothing.
 *
 * **There is exactly one interactive element in this card, and this is it.** The category is a
 * `<span>`, the bar is a `<span role="progressbar">`, and neither takes focus — so the overlay
 * cannot produce a link inside a link. Anything interactive added here later has to lift itself
 * above `.hit` and out of its way, the way the roster's `⋯` cell does.
 *
 * The address and the accessible name both arrive from the caller: `shared/` names no route and
 * holds no `t`. `link` is `null` on a card that leads nowhere, and a `null` card is not a link, does
 * not take focus and does not wear a pointer — which is the whole of the empty state's behaviour.
 */

import { Link, type LinkProps } from '@tanstack/react-router';
import { motion } from 'motion/react';

import { screenMotion } from '~/shared/design-system/motion';
import { planCategoryColour, type PlanCategoryColourKey } from '~/shared/ui/planCategoryColour';

import styles from './PlanCard.module.css';
import photoBox from './photoBox.module.css';

export interface PlanCardView {
  readonly name: string;
  /** The server's resolved cover, or `null`. Never a bundled substitute. */
  readonly coverImageUrl: string | null;
  /** The category as a KEY, which picks the chip's hue. `null` takes the neutral chip. */
  readonly categoryKey: PlanCategoryColourKey | null;
  /** The category in the trainer's language. `null` draws no chip at all. */
  readonly categoryLabel: string | null;
  /** "Tydzień 4 z 8", or the self-paced plan's cycles. Already written — see the header. */
  readonly weeksLabel: string;
  /** "jeszcze 5 dni", or `null` for a plan the contract gives no horizon for. */
  readonly endsLabel: string | null;
  readonly paused: boolean;
  /**
   * 0–100, or `null` for a plan with no week position — every self-paced one. The track is omitted
   * rather than drawn empty: a bar at zero reports a plan nobody has started.
   */
  readonly progressPct: number | null;
  /**
   * The day the plan ends, worded — „do 18 paź" — under the bar, where the caller can derive it.
   * Only the summary column draws it (KANON „Karty zadań" `planColumnCard`, 24 September 2026).
   */
  readonly endDateLabel?: string | null;
}

/**
 * Where the card leads, and what a screen reader hears on the way.
 *
 * `route` is the router's own `LinkProps`, built at the call site — which is what keeps the address
 * checked against the route tree while this file stays ignorant of every path in the app. `label`
 * is the accessible name and it has work to do: the card's own text says "PPL Build×4", which names
 * the plan and not the destination, so the label says where the link goes and whose plan it is.
 */
export interface PlanCardLink {
  readonly route: LinkProps;
  readonly label: string;
}

interface PlanCardProps {
  /**
   * One class for the card's root, on top of its own treatment. It exists for the skeleton
   * hand-over — `useSkeletonGate` hands out `skel.reveal` and it has to land on the element the
   * answer brings with it, which on the client's summary column is this card. No geometry comes
   * through it; the card's box stays this component's.
   */
  readonly className?: string;
  readonly plan: PlanCardView;
  /** The section's own name, in the caller's words — "Aktywny plan". */
  readonly eyebrow: string;
  /** The scheduling line under the name, already written: "Styl: Kalendarz". */
  readonly styleLine: string;
  /** What stands where the horizon would, for a paused plan. */
  readonly pausedLabel: string;
  /** `column` is the detail's 360px summary column; `panel` is the roster's preview. */
  readonly size: 'column' | 'panel';
  /** Where the whole card leads. `null` for a card that is not a link — see the header. */
  readonly link: PlanCardLink | null;
  /** The viewer asked for reduced motion. Required — see `shared/design-system/motion`. */
  readonly reducedMotion: boolean;
}

export function PlanCard({
  plan,
  eyebrow,
  styleLine,
  pausedLabel,
  size,
  link,
  reducedMotion,
  className,
}: PlanCardProps) {
  return (
    <section
      aria-label={eyebrow}
      className={`${String(styles.card)} ${size === 'panel' ? String(styles.panel) : String(styles.column)} ${className ?? ''}`}
    >
      {/* `photoBox.painted` carries the ground, the position and the scale; `styles.cover` carries
        * the height and the layout. The height is what makes this card the same 172px whether the
        * photograph arrives at once, arrives late, or never arrives — a background image
        * contributes nothing to layout, so there is no reservation to get wrong here and no
        * placeholder element to add. See `shared/ui/photoBox.module.css`. */}
      <div
        className={`${String(photoBox.painted)} ${String(styles.cover)}`}
        style={
          plan.coverImageUrl === null ? undefined : { backgroundImage: `url(${plan.coverImageUrl})` }
        }
      >
        {/* v3 fades the photograph into the card with an 18px `backdrop-filter`. Glass is banned
          * outright (CLAUDE.md), so the same legibility is reached with a scrim and photo ink. */}
        <span aria-hidden="true" className={styles.scrim} />
        <span className={styles.heroText}>
          {plan.categoryLabel === null ? null : (
            <span className={styles.chip} style={planCategoryColour(plan.categoryKey)}>
              <i aria-hidden="true" className={styles.chipDot} />
              {plan.categoryLabel}
            </span>
          )}
          <b className={styles.name}>{plan.name}</b>
          {/* How the plan is scheduled. It is a fact the trainer needs BEFORE reading the week
            * counter, because a self-paced plan's "week 6 of 8" counts cycles the client reached
            * rather than weeks that have passed. */}
          <span className={styles.mode}>{styleLine}</span>
        </span>
      </div>

      <div className={styles.body}>
        {/* The column's card is named by its section's label alone: its canon draws no eyebrow
          * between the photograph and the week (KANON „Karty zadań", 24 September 2026). */}
        {size === 'panel' ? <em className={styles.eyebrow}>{eyebrow}</em> : null}
        <p className={styles.weeks}>
          <b className={styles.weeksValue}>{plan.weeksLabel}</b>
          {plan.paused ? (
            <em className={styles.paused}>{pausedLabel}</em>
          ) : plan.endsLabel === null ? null : (
            <em className={styles.paused}>{plan.endsLabel}</em>
          )}
        </p>
        {plan.progressPct === null ? null : (
          <span
            aria-label={plan.weeksLabel}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={plan.progressPct}
            className={styles.track}
            role="progressbar"
          >
            <motion.i
              className={styles.fill}
              style={{ inlineSize: `${String(plan.progressPct)}%`, transformOrigin: 'left center' }}
              {...screenMotion('growX', { reducedMotion })}
            />
          </span>
        )}
        {plan.endDateLabel == null ? null : <span className={styles.endDate}>{plan.endDateLabel}</span>}
      </div>

      {/* Last, so it sits over the cover and the body without either needing a z-index of its own,
        * and so the reading order still ends on the card's own content. It carries no text: its
        * name is `label`, which is a complete sentence about a destination rather than a repeat of
        * the heading it sits on. */}
      {link === null ? null : <Link {...link.route} aria-label={link.label} className={styles.hit} />}
    </section>
  );
}
